import React from 'react';
import { appendItem, moveItem, patchItem, removeItem } from './helpers';

/**
 * Repeated rows with add / remove / reorder, shared by every section editor
 * that edits a list (list_item rows, Q&A pairs, dataset table rows).
 *
 * The caller owns the array: onChange is always called with the whole next
 * array, never a mutation. renderItem({ item, index, update, id }) draws one
 * row's fields; `update(patch)` merges a patch into that item, so keys the
 * editor does not know about survive.
 */
export default function ItemListEditor({
  idPrefix,
  label,
  items,
  onChange,
  renderItem,
  makeItem,
  addLabel = '+ Add item',
  itemLabel = (i) => `Item ${i + 1}`,
  emptyText = 'Nothing here yet.',
  hint,
}) {
  const list = Array.isArray(items) ? items : [];

  return (
    <div className="field">
      {label && (
        <div className="card-header" style={{ marginBottom: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            {label} <span style={{ fontWeight: 400, color: 'var(--color-muted)' }}>({list.length})</span>
          </span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => onChange(appendItem(list, makeItem()))}>
            {addLabel}
          </button>
        </div>
      )}

      {hint && <span className="hint" style={{ display: 'block', marginBottom: 8 }}>{hint}</span>}

      {list.length === 0 ? (
        <p className="hint" style={{ margin: '4px 0 8px' }}>{emptyText}</p>
      ) : (
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
          {list.map((item, index) => {
            const rowId = `${idPrefix}-${index}`;
            return (
              /* eslint-disable-next-line react/no-array-index-key */
              <li key={rowId} style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: 10 }}>
                <div className="card-header" style={{ marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: 'var(--color-muted)', fontWeight: 600 }}>{itemLabel(index, item)}</span>
                  <span style={{ display: 'inline-flex', gap: 2 }}>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => onChange(moveItem(list, index, index - 1))}
                      disabled={index === 0}
                      aria-label={`Move ${itemLabel(index, item)} up`}
                      title="Move up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => onChange(moveItem(list, index, index + 1))}
                      disabled={index === list.length - 1}
                      aria-label={`Move ${itemLabel(index, item)} down`}
                      title="Move down"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm btn-danger-text"
                      onClick={() => onChange(removeItem(list, index))}
                      aria-label={`Remove ${itemLabel(index, item)}`}
                      title="Remove"
                    >
                      ✕
                    </button>
                  </span>
                </div>
                {renderItem({
                  item,
                  index,
                  id: rowId,
                  update: (patch) => onChange(patchItem(list, index, patch)),
                })}
              </li>
            );
          })}
        </ol>
      )}

      {list.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => onChange(appendItem(list, makeItem()))}>
            {addLabel}
          </button>
        </div>
      )}
    </div>
  );
}
